import { FC, RefObject, useCallback, useEffect } from 'react';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { Article } from '@sharee/article-transcode';
import { useAtomValue } from 'jotai';
import { $CurrentArticleAsAvailable } from '~/routes/$user_link_name_.$article_id/shared/atoms/current-article';
import { ClickPosition } from '~/utils/click-position';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';
import { useNavigate, useSearchParams } from '@remix-run/react';
import { EArticleClass } from 'shared/models';
import { filterXSS } from '@lemon8/web-app-shared/util';

import './style.scss';

interface IArticleContentProps {
  ref?: RefObject<Article<any>>;
}

export const ArticleContent: FC<IArticleContentProps> = (props) => {
  const { ref } = props;
  const article = useAtomValue($CurrentArticleAsAvailable);
  const activeToCurrentArticle = useActiveToCurrentArticle();
  const navigate = useNavigate();
  const { title, content, groupId, articleClass, articleRegion } = article;

  const { isMobile, isBot, isInApp } = useAtomValue($UserAgent);
  const isRealUser = !isBot && isMobile && !isInApp;
  const [search] = useSearchParams();

  const handleAnchorClick = (
    position: ClickPosition,
    id: string,
    anchorType: string,
    customParams: Record<string, any>,
  ) => {
    event?.preventDefault();
    let path = '';
    switch (anchorType) {
      case 'poiAnchor':
      case 'poiInContent':
        const { poiId = '' } = customParams;
        path = `/poi/${poiId}`;
        break;
      case 'hashtagInContent':
        const { hashtagId = '' } = customParams;
        path = `/topic/${hashtagId}`;
        break;
      case 'mentionedUser':
        const { mentionedId = '' } = customParams;
        path = `/user/${mentionedId}/share`;
        break;
      default:
        break;
    }
    window.Tea('wap_click', {
      current_position: position,
      is_cta: 1,
      page_click_id: id,
    });
    if (isRealUser) {
      activeToCurrentArticle(position, id);
    } else {
      navigate(`${path}?region=${articleRegion}`);
    }
  };

  const handleHashtagClick = useCallback(
    (pathName: string) => {
      const hashtagId = pathName.replace('/topic/', '');
      handleAnchorClick(ClickPosition.main_hashtag, hashtagId, 'hashtagInContent', { hashtagId });
    },
    [handleAnchorClick],
  );
  const handlePoiClick = useCallback(
    (pathName: string) => {
      const poiId = pathName.replace('/poi/', '');
      handleAnchorClick(ClickPosition.main_content_poi, poiId, 'poiInContent', { poiId });
    },
    [handleAnchorClick],
  );
  const handleMentionedClick = useCallback(
    (mentionedHref: string, mentionedId: string) => {
      handleAnchorClick(ClickPosition.main_content_mentioned_user, mentionedId, 'mentionedUser', { mentionedId });
    },
    [handleAnchorClick],
  );

  const handleLinkClick = useCallback(
    (data: any, cardType: string) => {
      window.Tea('wap_click', {
        current_position: ClickPosition.main_inner_link,
        is_cta: 1,
      });
      // 点击跳转逻辑在 基类 中已经处理
      if (cardType === 'menuitem') {
        return;
      }
      // 控制文章内容中链接跳转，wap跳转，ug不跳转
      if (data && data.href) {
        window.open(data.href);
      }
    },
    [handleAnchorClick],
  );

  if (search.get('hide_article')) {
    return null;
  }

  return (
    <div className="article-content-wrapper">
      <Article
        articleTitle={title}
        isSSR={__BUILD_TARGET__ === 'server'}
        ref={ref}
        // show menu
        isShow={articleClass === 'Article'}
        groupId={groupId}
        countryRegion={articleRegion}
        onLinkClick={handleLinkClick}
        onHashtagClick={handleHashtagClick}
        onPoiClick={handlePoiClick}
        onMentionedClick={handleMentionedClick}
      >
        {article.articleClass === EArticleClass.ARTICLE ? content : filterXSS(content)}
      </Article>
    </div>
  );
};
